import * as React from "react";
import { cn } from "@/shared/utils";
import { FormHint } from "./FormHint";

export type ErrorMessage = string | undefined;

interface FormFieldProps {
    children: React.ReactNode;
    error?: ErrorMessage;
    success?: React.ReactNode;
    hint?: string | React.ReactNode;
    errorId?: string;
    className?: string;
    formHintProps?: React.HTMLAttributes<HTMLDivElement>;
    showGiftCardWrapper?: boolean;
    errorAction?: React.ReactNode;
}

type HintVariant = "error" | "success" | "hint";

const FormField = (props: FormFieldProps) => {
    const { children, error, success, hint, className, errorId, showGiftCardWrapper, errorAction } = props;
    let hintText: string | React.ReactNode = hint;
    let hintVariant: HintVariant = "hint";
    let hintAttributes = {};

    // Set hintText and hintVariant based on the presence of error or success props
    if (error) {
        hintText = error;
        hintVariant = "error";
        hintAttributes = { id: errorId, role: "alert", "aria-live": "polite" };
    } else if (success) {
        hintText = success;
        hintVariant = "success";
    }

    if (error) {
        hintText = error;
        hintVariant = "error";
        hintAttributes = { id: errorId, role: "alert", "aria-live": "polite" };
    } else if (success) {
        hintText = success;
        hintVariant = "success";
    }

    const getHintRow = () => {
        const hasHintText = !!hintText;
        const hasErrorAction = !!(error && errorAction);

        // Case 1: Both hintText and errorAction exist - flex row with justify-between
        if (hasHintText && hasErrorAction) {
            return (
                <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <FormHint className="mt-0 text-sm" variant={hintVariant} {...hintAttributes}>
                        {hintText}
                    </FormHint>
                    {errorAction}
                </div>
            );
        }

        // Case 2: Only hintText exists - backwards compatible
        if (hasHintText) {
            return (
                <FormHint variant={hintVariant} {...hintAttributes}>
                    {hintText}
                </FormHint>
            );
        }

        // Case 3: Only errorAction exists - just the action
        if (hasErrorAction) {
            return <div>{errorAction}</div>;
        }

        // Case 4: Neither exists - return null
        return null;
    };

    return (
        <div className={cn("relative flex flex-col", className)}>
            {children}
            {getHintRow()}
        </div>
    );
};

FormField.displayName = "FormField";

export { FormField };
